import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Row,
  Segmented,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { BarChart3, DollarSign, Download, RotateCcw } from 'lucide-react';
import type { ColumnsType } from 'antd/es/table';
import RunDetailModal from '../components/usage/RunDetailModal';
import { useGenerationRuns } from '../hooks/useGenerationRuns';
import { apiService } from '../services/api';
import { useTenantStore } from '../stores/useTenantStore';
import type { GenerationRun, RunLogEntry } from '../types';
import {
  formatCurrency,
  formatDuration,
  getPhaseLabel,
  getProviderMeta,
  getRunStatusColor,
} from '../components/usage/providerMeta';

const { Title, Text, Paragraph } = Typography;

type TimeFilter = '24h' | '7d' | '30d' | 'all';
type RunStatusFilter = 'all' | 'running' | 'completed' | 'aborted' | 'failed';
type RunSort = 'date_desc' | 'cost_desc' | 'duration_desc';

type ProviderBreakdown = {
  provider: string;
  spend: number;
  tokens: number;
  characters: number;
  count: number;
};

type ModelBreakdown = {
  model: string;
  provider: string;
  spend: number;
  invocations: number;
};

const costSourceColors: Record<string, string> = {
  api_response: 'green',
  calculated: 'blue',
  estimated: 'orange',
};

// AGCO-258 cost-basis presentation.
const COST_BASIS_LABEL: Record<string, string> = {
  subscription: 'Subscription',
  metered: 'Metered',
  unknown: 'Unknown',
  'n/a (non-writer)': 'Non-writer',
};
const COST_BASIS_TAG_COLOR: Record<string, string> = {
  subscription: 'blue',
  metered: 'green',
  unknown: 'orange',
  'n/a (non-writer)': 'default',
};

// Small costs need more decimals to be legible; large ones don't.
const formatCost = (value: number): string =>
  value > 0 && value < 1 ? value.toFixed(4) : value.toFixed(2);

// Human labels for usage phases — generation phases + the non-generation AI
// "features" surfaced by the cost instrumentation (AGCO-266). Mirrors the
// backend _PHASE_LABELS in api/routes/usage.py.
const PHASE_LABELS: Record<string, string> = {
  research: 'Research', data_collection: 'Data Collection', strategy: 'Strategy',
  writing: 'Writing', images: 'Images', qa: 'QA', seo: 'SEO', links: 'Links',
  assembly: 'Assembly', evaluation: 'Evaluation', publishing: 'Publishing', other: 'Other',
  chat: 'Emjae Chat', suggestion: 'Suggestor', audit: 'Technical Audit',
  metadata: 'Post-gen Metadata', ads: 'Google Ads AI', judge: 'Content Judge', editor: 'Editor AI',
  content_assist: 'Content Assist (CTA/FAQ)', verify: 'Verify (QA)',
};
// Phases that are non-generation AI features (vs core generation phases).
const FEATURE_PHASES = new Set([
  'chat', 'suggestion', 'audit', 'metadata', 'ads', 'judge', 'editor',
  'content_assist', 'verify',
]);
const phaseLabel = (phase: string): string =>
  PHASE_LABELS[phase] ?? phase.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const getTimeCutoff = (timeFilter: TimeFilter): number | null => {
  const now = Date.now();
  if (timeFilter === '24h') return now - 24 * 60 * 60 * 1000;
  if (timeFilter === '7d') return now - 7 * 24 * 60 * 60 * 1000;
  if (timeFilter === '30d') return now - 30 * 24 * 60 * 60 * 1000;
  return null;
};

const UsagePage: React.FC = () => {
  const currentTenant = useTenantStore((state) => state.currentTenant);
  const [usageLogs, setUsageLogs] = useState<RunLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [modelFilter, setModelFilter] = useState<string>('all');
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('7d');
  const [runStatusFilter, setRunStatusFilter] = useState<RunStatusFilter>('all');
  const [runSort, setRunSort] = useState<RunSort>('date_desc');
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const {
    data: generationRuns = [],
    isLoading: runsLoading,
    error: runsError,
    refetch: refetchRuns,
  } = useGenerationRuns({ sort: 'date_desc', limit: 25, tenant_id: currentTenant || undefined });
  React.useEffect(() => {
    void loadUsageLogs();
  }, [currentTenant]);

  React.useEffect(() => {
    if (modelFilter === 'all') return;
    const stillExists = usageLogs.some((log) => {
      if (providerFilter !== 'all' && log.provider !== providerFilter) {
        return false;
      }
      return log.model === modelFilter;
    });
    if (!stillExists) {
      setModelFilter('all');
    }
  }, [modelFilter, providerFilter, usageLogs]);

  const loadUsageLogs = async () => {
    setLoading(true);
    try {
      const data = (await apiService.getUsageLogs(currentTenant || undefined)) as RunLogEntry[];
      setUsageLogs(data);
    } catch (error) {
      console.error('Failed to load usage log entries', error);
      message.error('Unable to load usage log entries');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    await Promise.allSettled([loadUsageLogs(), refetchRuns()]);
  };

  const runMap = useMemo(() => {
    const map = new Map<string, GenerationRun>();
    generationRuns.forEach((run) => map.set(run.id, run));
    return map;
  }, [generationRuns]);

  const selectedRun = useMemo(
    () => (selectedRunId ? runMap.get(selectedRunId) || null : null),
    [runMap, selectedRunId]
  );

  const uniqueProviders = useMemo(() => {
    return Array.from(new Set(usageLogs.map((log) => log.provider).filter(Boolean)));
  }, [usageLogs]);

  const modelOptions = useMemo(() => {
    const models = new Set<string>();
    usageLogs.forEach((log) => {
      if (providerFilter !== 'all' && log.provider !== providerFilter) {
        return;
      }
      if (log.model) {
        models.add(log.model);
      }
    });
    return Array.from(models.values());
  }, [usageLogs, providerFilter]);

  const filteredLogs = useMemo(() => {
    const cutoff = getTimeCutoff(timeFilter);

    return [...usageLogs]
      .filter((log) => {
        if (providerFilter !== 'all' && log.provider !== providerFilter) {
          return false;
        }
        if (modelFilter !== 'all' && log.model !== modelFilter) {
          return false;
        }
        if (cutoff) {
          if (!log.timestamp) return false;
          const timestamp = new Date(log.timestamp).getTime();
          if (Number.isNaN(timestamp) || timestamp < cutoff) {
            return false;
          }
        }
        return true;
      })
      .sort((a, b) => {
        const tsA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const tsB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return tsB - tsA;
      });
  }, [usageLogs, providerFilter, modelFilter, timeFilter]);

  const filteredRuns = useMemo(() => {
    const cutoff = getTimeCutoff(timeFilter);
    const combinedRuns = [...generationRuns];

    return combinedRuns
      .filter((run) => {
        if (runStatusFilter !== 'all' && run.status !== runStatusFilter) {
          return false;
        }
        if (cutoff && run.started_at) {
          const timestamp = new Date(run.started_at).getTime();
          if (Number.isNaN(timestamp) || timestamp < cutoff) {
            return false;
          }
        }
        return true;
      })
      .sort((a, b) => {
        if (runSort === 'cost_desc') {
          return (b.total_cost_usd || 0) - (a.total_cost_usd || 0);
        }
        if (runSort === 'duration_desc') {
          return (b.duration_seconds || 0) - (a.duration_seconds || 0);
        }
        const tsA = a.started_at ? new Date(a.started_at).getTime() : 0;
        const tsB = b.started_at ? new Date(b.started_at).getTime() : 0;
        return tsB - tsA;
      });
  }, [generationRuns, runSort, runStatusFilter, timeFilter]);

  const totalSpend = useMemo(
    () => filteredLogs.reduce((sum, log) => sum + (log.cost_usd || 0), 0),
    [filteredLogs]
  );

  const totalRunSpend = useMemo(
    () => filteredRuns.reduce((sum, run) => sum + (run.total_cost_usd || 0), 0),
    [filteredRuns]
  );

  const totalTokens = useMemo(
    () => filteredLogs.reduce((sum, log) => sum + (log.total_tokens ?? log.input_tokens ?? 0), 0),
    [filteredLogs]
  );

  // AGCO-258: split the logs' recorded spend into REAL marginal vs list-price
  // NOTIONAL, broken out by how each row was billed. Mirrors the backend
  // aggregation so a subscription $0 reads as a flat fee, not free or broken.
  // unknownCount is the tripwire — a spike means the classifier stopped seeing
  // billing lanes (the failure mode that made this feature a no-op once before).
  const costBasis = useMemo(() => {
    let real = 0;
    let notional = 0;
    let unknownCount = 0;
    const byBasis: Record<string, { real: number; notional: number; count: number }> = {};
    filteredLogs.forEach((log) => {
      const cost = log.cost_usd || 0;
      // notional falls back to cost so metered/legacy rows never drop out of the
      // list-price total (Σ notional ≥ Σ real).
      const notionalCost = log.notional_cost_usd ?? cost;
      // Mirror the backend basis_bucket: an explicit basis is used as-is; a
      // WRITER row with no basis is a classification miss (tripwire); any other
      // NULL is a non-writer producer (exa/dataforseo/fal) — bucketed apart so
      // it never drowns the signal (Viktor review C3).
      const isWriter = log.cost_source === 'agent_sdk_result_message';
      let basis: string;
      let isWriterMiss = false;
      if (log.cost_basis) {
        basis = log.cost_basis;
        isWriterMiss = basis === 'unknown';
      } else if (isWriter) {
        basis = 'unknown';
        isWriterMiss = true;
      } else {
        basis = 'n/a (non-writer)';
      }
      real += cost;
      notional += notionalCost;
      const slot = (byBasis[basis] ??= { real: 0, notional: 0, count: 0 });
      slot.real += cost;
      slot.notional += notionalCost;
      slot.count += 1;
      if (isWriterMiss) unknownCount += 1;
    });
    return { real, notional, unknownCount, byBasis };
  }, [filteredLogs]);

  const providerBreakdown = useMemo<ProviderBreakdown[]>(() => {
    const map = new Map<string, ProviderBreakdown>();
    filteredLogs.forEach((log) => {
      if (!map.has(log.provider)) {
        map.set(log.provider, {
          provider: log.provider,
          spend: 0,
          tokens: 0,
          characters: 0,
          count: 0,
        });
      }
      const entry = map.get(log.provider)!;
      entry.spend += log.cost_usd || 0;
      entry.tokens += log.total_tokens ?? log.input_tokens ?? 0;
      entry.characters += log.characters_processed || 0;
      entry.count += 1;
    });
    return Array.from(map.values()).sort((a, b) => b.spend - a.spend);
  }, [filteredLogs]);

  const modelBreakdown = useMemo<ModelBreakdown[]>(() => {
    const map = new Map<string, ModelBreakdown>();
    filteredLogs.forEach((log) => {
      if (!log.model) return;
      const key = `${log.provider}-${log.model}`;
      if (!map.has(key)) {
        map.set(key, {
          model: log.model,
          provider: log.provider,
          spend: 0,
          invocations: 0,
        });
      }
      const entry = map.get(key)!;
      entry.spend += log.cost_usd || 0;
      entry.invocations += 1;
    });
    return Array.from(map.values()).sort((a, b) => b.spend - a.spend);
  }, [filteredLogs]);

  const phaseBreakdown = useMemo(() => {
    const map = new Map<
      string,
      { phase: string; spend: number; notional: number; tokens: number; count: number }
    >();
    filteredLogs.forEach((log) => {
      const phase = (log.phase as string) || 'unknown';
      if (!map.has(phase)) {
        map.set(phase, { phase, spend: 0, notional: 0, tokens: 0, count: 0 });
      }
      const entry = map.get(phase)!;
      const cost = log.cost_usd || 0;
      entry.spend += cost;
      // notional falls back to real so a phase never drops out of the list total.
      entry.notional += log.notional_cost_usd ?? cost;
      entry.tokens += log.total_tokens ?? log.input_tokens ?? 0;
      entry.count += 1;
    });
    // Sort by notional (list) — real is 0 for subscription rows, so sorting on it
    // would bury the writer; notional is the honest magnitude.
    return Array.from(map.values()).sort((a, b) => b.notional - a.notional);
  }, [filteredLogs]);

  const trendByDay = useMemo(() => {
    const map = new Map<string, { spend: number; tokens: number; count: number }>();
    filteredLogs.forEach((log) => {
      if (!log.timestamp) return;
      const dayKey = new Date(log.timestamp).toISOString().split('T')[0];
      if (!map.has(dayKey)) {
        map.set(dayKey, { spend: 0, tokens: 0, count: 0 });
      }
      const entry = map.get(dayKey)!;
      entry.spend += log.cost_usd || 0;
      entry.tokens += log.total_tokens ?? log.input_tokens ?? 0;
      entry.count += 1;
    });
    return Array.from(map.entries())
      .map(([date, values]) => ({ date, ...values }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [filteredLogs]);

  const handleExport = () => {
    if (!filteredLogs.length) {
      message.warning('No usage entries to export');
      return;
    }

    const headers = [
      'id',
      'run_id',
      'phase',
      'provider',
      'model',
      'timestamp',
      'input_tokens',
      'output_tokens',
      'total_tokens',
      'characters_processed',
      'cost_usd',
      'notional_cost_usd',
      'cost_basis',
      'cost_source',
    ];
    const rows = filteredLogs.map((log) =>
      [
        log.id,
        log.run_id || '',
        log.phase || '',
        log.provider,
        log.model,
        log.timestamp || '',
        log.input_tokens ?? '',
        log.output_tokens ?? '',
        log.total_tokens ?? '',
        log.characters_processed ?? '',
        log.cost_usd ?? '',
        log.notional_cost_usd ?? '',
        log.cost_basis ?? '',
        log.cost_source ?? '',
      ]
        .map((value) => (typeof value === 'string' ? value.replace(/"/g, '""') : value))
        .join(',')
    );

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `usage-log-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const columns: ColumnsType<RunLogEntry> = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (value: string | undefined) => (value ? new Date(value).toLocaleString() : '—'),
      sorter: (a, b) => {
        const tsA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const tsB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return tsA - tsB;
      },
      defaultSortOrder: 'descend',
    },
    {
      title: 'Run',
      key: 'run',
      render: (_, record) => {
        const run = record.run_id ? runMap.get(record.run_id) : null;
        return run ? (
          <Button type="link" size="small" style={{ paddingInline: 0 }} onClick={() => setSelectedRunId(run.id)}>
            {(run.content_id || run.id).slice(0, 8)}
          </Button>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      render: (provider: string) => {
        const meta = getProviderMeta(provider);
        return (
          <Tag color={meta.color}>
            <Space size={4}>
              {meta.icon}
              <span>{meta.label}</span>
            </Space>
          </Tag>
        );
      },
    },
    {
      title: 'Phase',
      dataIndex: 'phase',
      key: 'phase',
      render: (value: string | undefined) => (value ? <Tag>{getPhaseLabel(value)}</Tag> : '—'),
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      render: (value: string | undefined) => value || '—',
    },
    {
      title: 'Total Tokens',
      dataIndex: 'total_tokens',
      key: 'total_tokens',
      align: 'right',
    },
    {
      title: 'Characters',
      dataIndex: 'characters_processed',
      key: 'characters_processed',
      align: 'right',
    },
    {
      title: 'Cost (USD)',
      dataIndex: 'cost_usd',
      key: 'cost_usd',
      align: 'right',
      // AGCO-258: real marginal on top; the list-price figure below it, struck
      // through, only when it differs (i.e. a subscription row zeroed to real 0).
      render: (value: number | undefined, row: RunLogEntry) => {
        const real = value ?? 0;
        const notional = row.notional_cost_usd ?? real;
        return (
          <div>
            <div>{formatCurrency(real)}</div>
            {notional > real + 1e-9 && (
              <Text delete type="secondary" style={{ fontSize: 12 }}>
                {formatCurrency(notional)}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: 'Basis',
      dataIndex: 'cost_basis',
      key: 'cost_basis',
      render: (value: string | undefined) =>
        value ? (
          <Tag color={COST_BASIS_TAG_COLOR[value] ?? 'default'} style={{ marginInlineEnd: 0 }}>
            {COST_BASIS_LABEL[value] ?? value}
          </Tag>
        ) : (
          '—'
        ),
    },
    {
      title: 'Cost Source',
      dataIndex: 'cost_source',
      key: 'cost_source',
      render: (value: string | undefined) =>
        value ? <Tag color={costSourceColors[value] || 'default'}>{value}</Tag> : '—',
    },
  ];

  return (
    <div>
      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        <div>
          <Title level={2} style={{ marginBottom: 8 }}>
            <DollarSign size={24} style={{ marginRight: 8 }} className="inline" /> Usage & Cost Intelligence
          </Title>
          <Paragraph type="secondary" style={{ maxWidth: 760 }}>
            Monitor OpenRouter, Exa, DataForSEO, and fal.ai cost attribution in one place. The dashboard combines raw usage logs
            with per-run cost rollups so you can inspect live generation spend, phase timing, and tool call counts without manual math.
          </Paragraph>
        </div>

        {costBasis.unknownCount > 0 && (
          <Alert
            type="warning"
            showIcon
            message={`${costBasis.unknownCount} usage ${costBasis.unknownCount === 1 ? 'row' : 'rows'} unclassified`}
            description="Their billing lane couldn't be determined, so their cost is left as recorded rather than assumed. Usually a new lane that needs a label — worth a look if this number climbs."
          />
        )}

        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card>
              <Statistic
                title="Total spend (real)"
                value={costBasis.real}
                precision={costBasis.real > 0 && costBasis.real < 1 ? 4 : 2}
                prefix="$"
              />
              {costBasis.notional > costBasis.real + 1e-9 ? (
                <Text type="secondary">
                  <Text delete type="secondary">${formatCost(costBasis.notional)}</Text> list-equivalent
                  <Tooltip title="Subscription-billed work has no per-run marginal cost (the flat fee is allocated separately); its list-price figure is kept as an on-demand reference. This total is list price for writer rows plus real charges for metered providers — hence 'list-equivalent', not pure list price.">
                    {' '}<span style={{ cursor: 'help', textDecoration: 'underline dotted' }}>why?</span>
                  </Tooltip>
                </Text>
              ) : (
                <Text type="secondary">Real marginal spend across {filteredRuns.length} runs</Text>
              )}
              <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {Object.entries(costBasis.byBasis)
                  .sort((a, b) => b[1].notional - a[1].notional)
                  .map(([basis, v]) => (
                    <Tag
                      key={basis}
                      color={COST_BASIS_TAG_COLOR[basis] ?? 'default'}
                      style={{ marginInlineEnd: 0 }}
                    >
                      {COST_BASIS_LABEL[basis] ?? basis} ${formatCost(v.real)}
                    </Tag>
                  ))}
              </div>
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card>
              <Statistic title="API Calls" value={filteredLogs.length} />
              <Text type="secondary">Tracked provider invocations</Text>
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card>
              <Statistic title="Total Tokens" value={totalTokens || 0} />
              <Text type="secondary">Prompt + completion volume</Text>
            </Card>
          </Col>
        </Row>

        <Card
          title="Recent Generation Runs"
          extra={
            <Space>
              <Select<RunStatusFilter>
                value={runStatusFilter}
                onChange={setRunStatusFilter}
                style={{ minWidth: 160 }}
                options={[
                  { label: 'All statuses', value: 'all' },
                  { label: 'Running', value: 'running' },
                  { label: 'Completed', value: 'completed' },
                  { label: 'Failed', value: 'failed' },
                  { label: 'Aborted', value: 'aborted' },
                ]}
              />
              <Select<RunSort>
                value={runSort}
                onChange={setRunSort}
                style={{ minWidth: 180 }}
                options={[
                  { label: 'Date (new → old)', value: 'date_desc' },
                  { label: 'Cost (high → low)', value: 'cost_desc' },
                  { label: 'Duration (slow → fast)', value: 'duration_desc' },
                ]}
              />
            </Space>
          }
        >
          {runsError ? (
            <Alert type="warning" showIcon message="Run tracking endpoints are not available yet." style={{ marginBottom: 16 }} />
          ) : null}

          <Table<GenerationRun>
            rowKey="id"
            dataSource={filteredRuns}
            loading={runsLoading}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            locale={{ emptyText: <Empty description="No generation runs for the selected filters" /> }}
            columns={[
              {
                title: 'Run',
                key: 'run',
                render: (_, run) => {
                  const displayId = (run.content_id || run.id).slice(0, 8);
                  const wf = run.workflow_version || '';
                  const taskType = wf.startsWith('probe') ? 'Probe' : wf.includes('template_content_crew') ? 'Blog' : wf.split(':')[0] || 'Generation';
                  return (
                    <Space size={4} direction="vertical" style={{ gap: 2 }}>
                      <Button type="link" size="small" style={{ paddingInline: 0 }} onClick={() => setSelectedRunId(run.id)}>
                        {displayId}
                      </Button>
                      <Tag style={{ fontSize: 11 }}>{taskType}</Tag>
                    </Space>
                  );
                },
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 110,
                render: (status: string) => <Tag color={getRunStatusColor(status)}>{status.toUpperCase()}</Tag>,
              },
              {
                title: 'Cost',
                dataIndex: 'total_cost_usd',
                key: 'cost',
                width: 100,
                align: 'right',
                render: (value: number | undefined) => formatCurrency(value),
              },
              {
                title: 'Duration',
                dataIndex: 'duration_seconds',
                key: 'duration',
                width: 110,
                render: (value: number | undefined) => formatDuration(value),
              },
              {
                title: 'Started',
                dataIndex: 'started_at',
                key: 'started_at',
                width: 170,
                render: (value: string | undefined) => (value ? new Date(value).toLocaleString() : '—'),
              },
              {
                title: '',
                key: 'actions',
                width: 80,
                render: (_, run) => (
                  <Button size="small" onClick={() => setSelectedRunId(run.id)}>Details</Button>
                ),
              },
            ]}
          />
        </Card>

        <Card>
          <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
            <Space wrap>
              <Select
                style={{ minWidth: 180 }}
                placeholder="Provider"
                value={providerFilter}
                onChange={setProviderFilter}
                options={[
                  { label: 'All providers', value: 'all' },
                  ...uniqueProviders.map((provider) => ({ label: provider, value: provider })),
                ]}
              />
              <Select
                style={{ minWidth: 180 }}
                placeholder="Model"
                value={modelFilter}
                onChange={setModelFilter}
                options={[
                  { label: 'All models', value: 'all' },
                  ...modelOptions.map((model) => ({ label: model, value: model })),
                ]}
                disabled={modelOptions.length === 0}
              />
              <Segmented
                options={[
                  { label: '24h', value: '24h' },
                  { label: '7d', value: '7d' },
                  { label: '30d', value: '30d' },
                  { label: 'All', value: 'all' },
                ]}
                value={timeFilter}
                onChange={(value) => setTimeFilter(value as TimeFilter)}
              />
            </Space>
            <Space>
              <Tooltip title="Refresh usage and run data">
                <Button icon={<RotateCcw size={14} />} onClick={() => void handleRefresh()} loading={loading || runsLoading} />
              </Tooltip>
              <Tooltip title="Export current view to CSV">
                <Button icon={<Download size={14} />} onClick={handleExport} disabled={!filteredLogs.length} />
              </Tooltip>
            </Space>
          </Row>

          <Table<RunLogEntry>
            rowKey="id"
            columns={columns}
            dataSource={filteredLogs}
            loading={loading}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            locale={{ emptyText: <Empty description="No usage logs yet" /> }}
          />
        </Card>

        <Card title="Provider Notes">
          <Row gutter={[16, 16]}>
            {['openrouter', 'exa', 'dataforseo', 'fal.ai'].map((provider) => {
              const meta = getProviderMeta(provider);
              return (
                <Col xs={24} md={12} xl={6} key={provider}>
                  <Card size="small">
                    <Space>
                      <Tag color={meta.color}>{meta.label}</Tag>
                    </Space>
                    <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                      {meta.description}
                    </Paragraph>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card>

        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Card title="Cost by Feature" extra={<BarChart3 size={14} />}>
              {phaseBreakdown.length === 0 ? (
                <Empty description="No usage yet — enable a USAGE_*_TRACKING flag to populate feature cost" />
              ) : (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {phaseBreakdown.map((p) => {
                    // Share is of the LIST (notional) total, because real is $0 for
                    // subscription-billed rows (the writer) — sharing on real would
                    // make the single most expensive feature read as ~0%.
                    const share = costBasis.notional ? (p.notional / costBasis.notional) * 100 : 0;
                    const isFeature = FEATURE_PHASES.has(p.phase);
                    // Only surface the real/COGS figure when it actually diverges
                    // from list (subscription rows), so metered phases stay quiet.
                    const realDiverges = p.notional > p.spend + 1e-9;
                    return (
                      <div
                        key={p.phase}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 0',
                          borderBottom: '1px solid #f0f0f0',
                        }}
                      >
                        <Space>
                          <Tag color={isFeature ? 'var(--color-coral)' : 'var(--color-teal)'}>
                            {isFeature ? 'Feature' : 'Generation'}
                          </Tag>
                          <div>
                            <Text strong>{phaseLabel(p.phase)}</Text>
                            <div>
                              <Text type="secondary">
                                {formatCurrency(p.notional)} list
                                {realDiverges && (
                                  <> · {formatCurrency(p.spend)} real</>
                                )}
                                {' · '}{p.count} calls · {p.tokens.toLocaleString()} tokens
                              </Text>
                            </div>
                          </div>
                        </Space>
                        <div style={{ textAlign: 'right' }}>
                          <Text>{share.toFixed(1)}%</Text>
                        </div>
                      </div>
                    );
                  })}
                </Space>
              )}
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card title="Spend by Provider" extra={<BarChart3 size={14} />}>
              {providerBreakdown.length === 0 ? (
                <Empty description="No provider data" />
              ) : (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {providerBreakdown.map((provider) => {
                    const share = totalSpend ? (provider.spend / totalSpend) * 100 : 0;
                    const meta = getProviderMeta(provider.provider);
                    return (
                      <div
                        key={provider.provider}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 0',
                          borderBottom: '1px solid #f0f0f0',
                        }}
                      >
                        <Space>
                          {meta.icon}
                          <div>
                            <Text strong>{meta.label}</Text>
                            <div>
                              <Text type="secondary">
                                {formatCurrency(provider.spend)} · {provider.tokens.toLocaleString()} tokens
                                {provider.characters ? ` · ${provider.characters.toLocaleString()} chars` : ''}
                              </Text>
                            </div>
                          </div>
                        </Space>
                        <div style={{ textAlign: 'right' }}>
                          <Text>{share.toFixed(1)}%</Text>
                        </div>
                      </div>
                    );
                  })}
                </Space>
              )}
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Top Models by Spend">
              {modelBreakdown.length === 0 ? (
                <Empty description="No model data" />
              ) : (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {modelBreakdown.slice(0, 5).map((model) => (
                    <div
                      key={`${model.provider}-${model.model}`}
                      style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}
                    >
                      <Space direction="vertical" size={0}>
                        <Text strong>{model.model}</Text>
                        <Text type="secondary">{model.provider}</Text>
                      </Space>
                      <div style={{ textAlign: 'right' }}>
                        <Text strong>{formatCurrency(model.spend)}</Text>
                        <div>
                          <Text type="secondary">{model.invocations} calls</Text>
                        </div>
                      </div>
                    </div>
                  ))}
                </Space>
              )}
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Card title="Daily Spend Trend">
              {trendByDay.length === 0 ? (
                <Empty description="No historical data" />
              ) : (
                <Row gutter={[16, 16]}>
                  {trendByDay.map((day) => (
                    <Col xs={24} md={12} key={day.date}>
                      <Card size="small" bordered={false} style={{ background: '#fafafa' }}>
                        <Text strong>{new Date(day.date).toLocaleDateString()}</Text>
                        <div>
                          <Text type="secondary">{formatCurrency(day.spend)}</Text>
                        </div>
                        <div>
                          <Text type="secondary">
                            {day.tokens.toLocaleString()} tokens · {day.count} calls
                          </Text>
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </Card>
          </Col>
        </Row>

      </Space>

      <RunDetailModal
        open={Boolean(selectedRunId)}
        runId={selectedRunId}
        fallbackRun={selectedRun}
        onClose={() => setSelectedRunId(null)}
      />
    </div>
  );
};

export default UsagePage;
